
import React from 'react';
import { motion } from 'framer-motion';
import { Terminal } from 'lucide-react';

interface TechTerminalDisplayProps {
  code: string;
  commandLine: string;
  color: string;
}

const TechTerminalDisplay: React.FC<TechTerminalDisplayProps> = ({ code, commandLine, color }) => {
  return (
    <div className="rounded-lg overflow-hidden border border-white/10 shadow-xl">
      {/* Terminal header */}
      <div className="bg-[#1E1E1E] px-4 py-2 flex items-center">
        <div className="flex space-x-2 mr-4">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <span className="text-white/70 text-xs flex-1 text-center font-mono">code.tsx</span>
      </div>
      
      {/* Code display */}
      <div className="bg-[#1E1E1E] p-4 font-mono text-sm relative overflow-hidden">
        {/* Line numbers */}
        <div className="absolute left-4 top-4 text-gray-600 select-none">
          {code.split('\n').map((_, i) => (
            <div key={i}>{i + 1}</div>
          ))}
        </div>
        
        {/* Code content with syntax highlighting */}
        <pre className="pl-8 text-gray-300 overflow-x-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {code.split('\n').map((line, i) => (
              <div key={i} className="whitespace-pre">
                {highlightSyntax(line)}
              </div>
            ))}
          </motion.div>
        </pre>
        
        {/* Blinking cursor */}
        <motion.div 
          className="absolute bottom-4 right-4 h-4 w-2"
          style={{ backgroundColor: color }}
          animate={{ opacity: [1, 0, 1] }}
          transition={{ repeat: Infinity, duration: 1 }}
        />
      </div>
      
      {/* Command line */}
      <div className="bg-black p-3 font-mono text-sm text-green-400 flex items-center">
        <Terminal size={14} className="mr-2" />
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="animate-typing"
        >
          $ {commandLine}
        </motion.span>
      </div>
    </div>
  );
};

// Simple syntax highlighting function
const highlightSyntax = (code: string) => {
  // Replace keywords with colored spans
  return code
    .replace(/(const|let|var|function|return|export|default|import|from|async|await|try|catch)/g, '<span style="color: #C586C0;">$1</span>')
    .replace(/(=&gt;|\{|\}|\(|\)|,|;|\.)/g, '<span style="color: #d4d4d4;">$1</span>')
    .replace(/"([^"]*)"/g, '<span style="color: #ce9178;">"$1"</span>')
    .replace(/'([^']*)'/g, '<span style="color: #ce9178;">\'$1\'</span>')
    .replace(/\/\/\s.*/g, '<span style="color: #6A9955;">$&</span>')
    .split('<span').join('__SPLIT__<span')
    .split('__SPLIT__')
    .map((part, i) => {
      if (part.startsWith('<span')) {
        return <span key={i} dangerouslySetInnerHTML={{ __html: part }} />;
      }
      return <span key={i} style={{ color: '#9CDCFE' }}>{part}</span>;
    });
};

export default TechTerminalDisplay;
